import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { campaignsApi, type AutopilotPlan, type MarketingCampaign } from '../api/campaignsApi';
import {
  CircleCheckBig,
  Rocket,
  Image as ImageIcon,
  Zap,
  ClipboardList,
  Check,
} from 'lucide-react';
import { ExtBadge, PNBadge } from './CampaignBadges';
import { ExistingCampaignRow, type ExistingCampaign } from './ExistingCampaignRow';
import styles from './GuidedWizard.module.css';

interface Props {
  recommendedCampaigns: MarketingCampaign[];
  existingCampaigns: ExistingCampaign[];
  onCancel: () => void;
  onSwitchToAutopilot: () => void;
}

type Step =
  | { kind: 'campaign'; index: number }
  | { kind: 'existing' }
  | { kind: 'summary' }
  | { kind: 'done' };

interface CampaignState {
  approved: boolean;
  monthlyBudget: number;
}

/**
 * Guided wizard — per-campaign carousel + existing-campaign triage + final
 * summary. Persistently nudges toward Autopilot via a top banner.
 */
export function GuidedWizard({
  recommendedCampaigns,
  existingCampaigns,
  onCancel,
  onSwitchToAutopilot,
}: Props) {
  const { currentLocation } = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [step, setStep] = useState<Step>({ kind: 'campaign', index: 0 });
  const [campaignState, setCampaignState] = useState<CampaignState[]>(
    recommendedCampaigns.map((c) => {
      const brief = (c.brief ?? {}) as { monthlyBudget?: number };
      return {
        approved: false,
        monthlyBudget: brief.monthlyBudget ?? 1500,
      };
    }),
  );
  const [keptIds, setKeptIds] = useState<string[]>(existingCampaigns.map((c) => c.id));
  const [editingBudgetIdx, setEditingBudgetIdx] = useState<number | null>(null);

  const totalSteps = recommendedCampaigns.length + 2; // campaigns + existing + summary
  const stepIndex =
    step.kind === 'campaign'
      ? step.index
      : step.kind === 'existing'
        ? recommendedCampaigns.length
        : step.kind === 'summary'
          ? recommendedCampaigns.length + 1
          : totalSteps;

  const approveAllMutation = useMutation({
    mutationFn: () => {
      const totalBudget = campaignState.reduce((s, c) => s + (c.approved ? c.monthlyBudget : 0), 0);
      const plan: AutopilotPlan = {
        locationId: currentLocation!.id,
        managementMode: 'guided',
        monthlyAdBudget:
          totalBudget +
          existingCampaigns
            .filter((c) => keptIds.includes(c.id))
            .reduce((s, c) => s + (c.dailyBudget ? c.dailyBudget * 30 : c.spend || 0), 0),
        keepExistingCampaignIds: keptIds,
        campaigns: recommendedCampaigns
          .map((rec, i) => ({ rec, cs: campaignState[i] }))
          .filter(({ cs }) => cs.approved)
          .map(({ rec, cs }) => {
            const brief = (rec.brief ?? {}) as Record<string, unknown> & { _rationale?: string };
            return {
              id: rec.id,
              name: rec.name,
              objective: (rec.objective ?? 'leads') as 'leads' | 'conversions' | 'awareness',
              monthlyBudget: cs.monthlyBudget,
              channelMix: { google_ppc: 60, google_pmax: 40 },
              aiRationale: brief._rationale ?? '',
              offerHook: (brief.offer as string | undefined) ?? '',
              cta: (brief.cta as string | undefined) ?? '',
              targetAudience: (brief.targetAudience as string | undefined) ?? '',
              keyMessages: (brief.keyMessages as string[] | undefined) ?? [],
              geo: (brief.geo as string | undefined) ?? '',
            };
          }),
      };
      return campaignsApi.autopilot.approve(plan);
    },
    onSuccess: () => {
      setStep({ kind: 'done' });
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Approve failed', 'error');
    },
  });

  function next() {
    if (step.kind === 'campaign') {
      if (step.index < recommendedCampaigns.length - 1) {
        setStep({ kind: 'campaign', index: step.index + 1 });
      } else if (existingCampaigns.length > 0) {
        setStep({ kind: 'existing' });
      } else {
        setStep({ kind: 'summary' });
      }
    } else if (step.kind === 'existing') {
      setStep({ kind: 'summary' });
    }
  }

  function back() {
    if (step.kind === 'campaign' && step.index > 0) {
      setStep({ kind: 'campaign', index: step.index - 1 });
    } else if (step.kind === 'existing') {
      setStep({ kind: 'campaign', index: recommendedCampaigns.length - 1 });
    } else if (step.kind === 'summary') {
      if (existingCampaigns.length > 0) setStep({ kind: 'existing' });
      else setStep({ kind: 'campaign', index: recommendedCampaigns.length - 1 });
    }
  }

  return (
    <div className={styles.wizard}>
      <div className={styles.topRow}>
        <span className={styles.stepCount}>
          {step.kind === 'summary'
            ? 'Final review'
            : step.kind === 'existing'
              ? 'Existing campaigns'
              : step.kind === 'campaign'
                ? `Campaign ${step.index + 1} of ${recommendedCampaigns.length}`
                : ''}
        </span>
        <button type="button" className={styles.autopilotNudge} onClick={onSwitchToAutopilot}>
          <Rocket size={14} aria-hidden /> Switch to Autopilot
        </button>
      </div>

      <div className={styles.progress}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`${styles.progressBar} ${i < stepIndex ? styles.progressBarDone : ''} ${
              i === stepIndex ? styles.progressBarOn : ''
            }`}
          />
        ))}
      </div>

      {step.kind === 'campaign' && (
        <div>
          {(() => {
            const rec = recommendedCampaigns[step.index];
            const brief = (rec.brief ?? {}) as {
              offer?: string;
              cta?: string;
              targetAudience?: string;
              geo?: string;
              _rationale?: string;
            };
            const cs = campaignState[step.index];
            return (
              <div className={styles.campaignCard}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardName}>{rec.name}</span>
                  <PNBadge />
                  {rec.objective && <span className={styles.typePill}>{rec.objective}</span>}
                </div>

                {brief._rationale && <p className={styles.rationale}>{brief._rationale}</p>}

                {[
                  ['Offer / Hook', brief.offer ?? '—'],
                  ['Target audience', brief.targetAudience ?? '—'],
                  ['Zip Codes', brief.geo ?? '—'],
                  ['Call to action', brief.cta ?? '—'],
                ].map(([label, value]) => (
                  <div key={label} className={styles.field}>
                    <div className={styles.fieldLabel}>{label}</div>
                    <div className={styles.fieldValue}>{value}</div>
                  </div>
                ))}

                <div className={styles.field}>
                  <div className={styles.fieldLabel}>Monthly budget</div>
                  <input
                    type="number"
                    className={styles.budgetInput}
                    value={cs.monthlyBudget}
                    onChange={(e) =>
                      setCampaignState((prev) =>
                        prev.map((p, i) =>
                          i === step.index ? { ...p, monthlyBudget: Number(e.target.value) } : p,
                        ),
                      )
                    }
                  />
                </div>

                <div className={styles.creativeNote}>
                  <ImageIcon size={14} aria-hidden /> Image creative will be created within 24 hours
                  after final approval.
                </div>
              </div>
            );
          })()}

          <div className={styles.actions}>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => (step.index > 0 ? back() : onCancel())}
            >
              ← Back
            </Button>
            <div className={styles.actionsRight}>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setCampaignState((prev) =>
                    prev.map((p, i) => (i === step.index ? { ...p, approved: false } : p)),
                  );
                  next();
                }}
              >
                Discard campaign
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setCampaignState((prev) =>
                    prev.map((p, i) => (i === step.index ? { ...p, approved: true } : p)),
                  );
                  next();
                }}
              >
                Okay to proceed →
              </Button>
            </div>
          </div>
        </div>
      )}

      {step.kind === 'existing' && (
        <div>
          <h2 className={styles.stepTitle}>Your existing campaigns</h2>
          <p className={styles.stepBody}>Keep or pause each one.</p>
          {existingCampaigns.map((c) => (
            <ExistingCampaignRow
              key={c.id}
              campaign={c}
              kept={keptIds.includes(c.id)}
              onKeep={() => setKeptIds((p) => Array.from(new Set([...p, c.id])))}
              onPause={() => setKeptIds((p) => p.filter((id) => id !== c.id))}
            />
          ))}
          <div className={styles.actions}>
            <Button variant="tertiary" size="sm" onClick={back}>
              ← Back
            </Button>
            <Button size="sm" onClick={next}>
              Review &amp; approve →
            </Button>
          </div>
        </div>
      )}

      {step.kind === 'summary' && (
        <div>
          <h2 className={styles.stepTitle}>Final review &amp; approve</h2>
          <p className={styles.stepBody}>Review all campaigns and budgets below.</p>

          <div className={styles.sectionLabel}>
            <Zap size={14} aria-hidden /> New strategy campaigns
          </div>
          {recommendedCampaigns.map((rec, i) => {
            const approved = campaignState[i].approved;
            return (
              <div
                key={rec.id}
                className={`${styles.summaryRow} ${!approved ? styles.summaryRowDiscarded : ''}`}
              >
                <div className={styles.cardHeader}>
                  <PNBadge />
                  <span className={styles.cardName}>{rec.name}</span>
                  {approved ? (
                    <span className={styles.okPill}>OK</span>
                  ) : (
                    <span className={styles.discardedPill}>Discarded</span>
                  )}
                </div>
                {approved && (
                  <div className={styles.summaryBudgetWrap}>
                    {editingBudgetIdx === i ? (
                      <input
                        type="number"
                        className={styles.budgetInlineInput}
                        value={campaignState[i].monthlyBudget}
                        onChange={(e) =>
                          setCampaignState((prev) =>
                            prev.map((p, j) =>
                              j === i ? { ...p, monthlyBudget: Number(e.target.value) } : p,
                            ),
                          )
                        }
                        onBlur={() => setEditingBudgetIdx(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingBudgetIdx(null)}
                        autoFocus
                      />
                    ) : (
                      <>
                        <span className={styles.summaryBudget}>
                          ${campaignState[i].monthlyBudget.toLocaleString()}/mo
                        </span>
                        <button
                          type="button"
                          className={styles.editLink}
                          onClick={() => setEditingBudgetIdx(i)}
                        >
                          edit
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {keptIds.length > 0 && (
            <>
              <div className={styles.sectionLabelExt}>
                <ClipboardList size={14} aria-hidden /> Existing campaigns (kept)
              </div>
              {existingCampaigns
                .filter((c) => keptIds.includes(c.id))
                .map((c) => (
                  <div key={c.id} className={styles.summaryRow}>
                    <div className={styles.cardHeader}>
                      {c.source === 'pn-ai' ? <PNBadge /> : <ExtBadge />}
                      <span className={styles.cardName}>{c.name}</span>
                    </div>
                    <div className={styles.summaryBudgetWrap}>
                      <span className={styles.summaryBudget}>
                        {c.dailyBudget
                          ? `$${Math.round(c.dailyBudget * 30).toLocaleString()}/mo`
                          : `$${c.spend.toFixed(0)}/mo`}
                      </span>
                    </div>
                  </div>
                ))}
            </>
          )}

          <div className={styles.timelineCard}>
            <div className={styles.timelineTitle}>What happens next</div>
            {[
              ['Today', 'You approve this plan'],
              [
                'Within 24 hours',
                'Our team will finalize ad copy and image creative for each new campaign',
              ],
              ['After 24 hours', "You'll need to approve each creative before launch"],
              ['Weekly', 'Performance updates in your dashboard'],
            ].map(([when, what]) => (
              <div key={when} className={styles.timelineRow}>
                <span className={styles.timelineWhen}>{when}</span>
                <span className={styles.timelineWhat}>{what}</span>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <Button variant="tertiary" size="sm" onClick={back}>
              ← Back
            </Button>
            <Button
              size="sm"
              onClick={() => approveAllMutation.mutate()}
              isLoading={approveAllMutation.isPending}
            >
              Approve plan <Check size={14} aria-hidden />
            </Button>
          </div>
        </div>
      )}

      {step.kind === 'done' && (
        <div className={styles.done}>
          <div className={styles.doneIcon}>
            <CircleCheckBig size={44} strokeWidth={1.5} color="var(--success)" />
          </div>
          <div className={styles.doneTitle}>Plan approved!</div>
          <p className={styles.doneBody}>Creative will be ready for your review within 24 hours.</p>
          <Button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] });
              onCancel();
            }}
          >
            Go to Overview →
          </Button>
        </div>
      )}
    </div>
  );
}
